<script setup>
import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import _ from "lodash";
import { computed, provide, ref, toValue, watch } from "vue";

import { useTenantEntityManager } from "@/composables/useTenantManager.js";
import { ERROR_TIMEOUT } from "@/constants";
import inferenceCoursesService from "@/services/inferenceCourses";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import ApproveCoursesFilterSummary from "../components/ApproveCoursesFilterSummary.vue";
import CourseSearchBar from "../components/CourseSearchBar.vue";
import CourseTable from "../components/CoursesTable/CoursesTable.vue";
import HeadingMenu from "../components/HeadingMenu.vue";
import NoResults from "../components/NoResults.vue";
import { INITIAL_INFERENCE_FILTERS } from "../constants";
import { queryKeys } from "../constants.js";

const inferenceFilters = ref({ ...INITIAL_INFERENCE_FILTERS });

provide("inferenceFilters", inferenceFilters);
const toast = useToast();

const store = useStore();
const currentPage = ref(1);
const searchInput = ref("");
const maxResults = ref(10);
const combinedTableData = ref([]);

const { getSelectedEntityView: selectedEntity } = useTenantEntityManager();

/* This is here to isolate the source of truth as far as entiies go for the CI app
and to reduce calls to profiles in the event there aren't entities from the okta token (hook is down/off)
*/
const selectedEntityId = computed(() => {
  return selectedEntity?.value?.id ? selectedEntity.value.id : store.getters.getEntityIds[0];
});

const hasEquivalencyResults = computed(() => {
  return courseEquivalencies.value?.items?.length > 0;
});

const enableSearch = computed(() => {
  return !!toValue(searchInput);
});

provide("selectedEntityId", selectedEntityId);

const {
  isLoading: isLoadingCourseEquivalencies,
  isFetching: isFetchingCourseEquivalencies,
  data: courseEquivalencies,
  isPlaceholderData,
  isError,
} = useQuery({
  queryKey: queryKeys.courseEquivalencies.list(currentPage, searchInput, selectedEntityId, inferenceFilters),
  queryFn: () =>
    inferenceCoursesService.getCourses(
      maxResults.value,
      currentPage.value,
      selectedEntityId.value,
      searchInput.value,
      inferenceFilters.value,
    ),
  gcTime: 0,
  placeholderData: keepPreviousData,
  enabled: enableSearch,
});
const combinedLoading = computed(() => {
  return isLoadingCourseEquivalencies.value || isFetchingCourseEquivalencies.value;
});

const truncatedSearchInput = computed(() => {
  return _.truncate(`${searchInput.value}`, {
    length: 100,
    omission: "...",
  });
});

watch([courseEquivalencies, isPlaceholderData], () => {
  const items = courseEquivalencies.value?.items ?? [];
  if (!isPlaceholderData.value) {
    if (currentPage.value === 1) {
      combinedTableData.value = [...items];
    } else {
      combinedTableData.value = combinedTableData.value.concat(items);
    }
  }
});
const isInitialLoading = computed(() => {
  return combinedLoading.value && combinedTableData.value.length === 0;
});
const resetPagination = () => {
  currentPage.value = 1;
  combinedTableData.value = [];
};
watch(isError, () => {
  if (isError.value) {
    toast.open({
      message: "Unable to load course equivalency information, please try again later. ",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
});
watch(
  [selectedEntityId, inferenceFilters],
  () => {
    resetPagination();
  },
  { deep: true },
);
const shouldShowTable = computed(() => {
  return hasEquivalencyResults.value && enableSearch.value && !isInitialLoading.value;
});

const getCourseEquivalencies = (userInput) => {
  if (searchInput.value !== userInput) {
    currentPage.value = 1;
    searchInput.value = userInput;
    combinedTableData.value = [];
  }
};
const hasNextPage = computed(() => {
  const totalItemCount = courseEquivalencies.value?.count;
  return totalItemCount && totalItemCount > combinedTableData.value.length;
});

const changePage = () => {
  if (!isPlaceholderData.value && hasNextPage.value) {
    currentPage.value = currentPage.value + 1;
  }
};
const hasNoResults = computed(() => {
  const isLoading = toValue(combinedLoading);
  const hasEmptyResults = toValue(combinedTableData).length === 0;
  return !isLoading && hasEmptyResults;
});
const shouldDisplayNoResults = computed(() => {
  return hasNoResults.value && enableSearch.value;
});
</script>

<template>
  <div class="p-4 bg-gray-30">
    <HeadingMenu heading-title="Global Course Navigator™" class="mt-4 mb-2" />
    <p class="mt-4 mb-2 text-lg">
      The Global Course Navigator is a curriculum integration tool that can match courses in your catalog with courses
      at universities abroad in seconds. You can review and approve matching courses, save the equivalent course data
      from your catalog for easy reference, and better match students with programs abroad.
    </p>
    <CourseSearchBar @search="getCourseEquivalencies" />
    <div v-if="searchInput" class="pt-4 font-semibold text-indigo-base">
      <p>Results for '{{ truncatedSearchInput }}'</p>
    </div>
    <div class="flex justify-between pt-5 pb-3">
      <button v-if="enableSearch" aria-label="Open filters" @click="openFilter">
        <ApproveCoursesFilterSummary />
      </button>
    </div>
    <CourseTable
      :should-show-table="shouldShowTable"
      :table-data="combinedTableData"
      :is-loading="combinedLoading"
      @update:current-page="changePage"
    />
    <NoResults v-if="shouldDisplayNoResults" />
  </div>
</template>
